// # Cache Manager for Session System
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import firestoreLogger from './firebase_logger';
import sessionManager from './session';

class CacheManager {
  constructor() {
    this._cache = new Map();
    this._expiryTimes = new Map();
    this.DEFAULT_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
    this._stats = {
      hits: 0,
      misses: 0,
      sets: 0,
      removes: 0,
      firestoreReads: 0
    };
    this._subscriptionCheckCache = new Map();
    this.SUBSCRIPTION_CHECK_DURATION = 24 * 60 * 60 * 1000; // 24 hours cache for subscription checks
    this._pendingRequests = new Map();
  }

  // # Get cache stats
  getStats() {
    return { ...this._stats };
  }

  // # Reset stats
  resetStats() {
    this._stats = {
      hits: 0,
      misses: 0,
      sets: 0,
      removes: 0,
      firestoreReads: 0
    };
  }

  // # Log cache operation
  _logOperation(operation, key, success = true) {
    // # Removed console logging for security and performance
    return {
      operation,
      key,
      success,
      timestamp: new Date().toISOString(),
      stats: this.getStats()
    };
  }

  // # Set cache with expiry
  set(key, value, duration = this.DEFAULT_CACHE_DURATION) {
    // # Don't set if value hasn't changed - Improved comparison
    const currentValue = this._cache.get(key);
    if (currentValue) {
      const currentJSON = JSON.stringify(currentValue);
      const newJSON = JSON.stringify(value);
      if (currentJSON === newJSON && 
          this._expiryTimes.get(key) > Date.now()) {
        return; // Skip if data is identical and not expired
      }
    }

    // # If there's a pending request for this key, wait for it
    if (this._pendingRequests.has(key)) {
      return;
    }

    this._cache.set(key, value);
    this._expiryTimes.set(key, Date.now() + duration);
    this._stats.sets++;
    this._logOperation('set', key);
  }

  // # Get cache if not expired with deduplication
  get(key) {
    // # Check if there's a pending request for this key
    if (this._pendingRequests.has(key)) {
      return this._pendingRequests.get(key);
    }

    if (!this._cache.has(key)) {
      this._stats.misses++;
      this._logOperation('get', key, false);
      return null;
    }
    
    const expiryTime = this._expiryTimes.get(key);
    if (Date.now() > expiryTime) {
      this.remove(key);
      this._stats.misses++;
      this._logOperation('get', key, false);
      return null;
    }
    
    this._stats.hits++;
    this._logOperation('get', key);
    return this._cache.get(key);
  }

  // # Remove cache entry
  remove(key) {
    this._cache.delete(key);
    this._expiryTimes.delete(key);
    this._stats.removes++;
    this._logOperation('remove', key);
  }

  // # Clear all cache
  clear() {
    this._cache.clear();
    this._expiryTimes.clear();
    this._logOperation('clear', 'all');
  }

  // # Initialize user data cache with deduplication
  async initializeUserCache(uid, forceRefresh = false) {
    const cacheKey = `user_${uid}`;
    
    // # Return cached data if available and not forcing refresh
    if (!forceRefresh) {
      const cachedData = this.get(cacheKey);
      if (cachedData) return cachedData;
    }

    // # Check for pending request
    if (this._pendingRequests.has(cacheKey)) {
      return this._pendingRequests.get(cacheKey);
    }

    try {
      // # Create promise for this request
      const promise = (async () => {
        const userDoc = await firestoreLogger.getDocumentWithLogging(doc(db, 'users', uid));
        const userData = userDoc.exists() ? userDoc.data() : null;

        if (userData) {
          this.set(cacheKey, userData);
        }
        
        return userData;
      })();

      // # Store promise in pending requests
      this._pendingRequests.set(cacheKey, promise);

      const result = await promise;
      this._pendingRequests.delete(cacheKey);
      return result;
    } catch (error) {
      this._pendingRequests.delete(cacheKey);
      console.error('Error initializing user cache:', error);
      return null;
    }
  }

  // # Initialize subscription cache with deduplication
  async initializeSubscriptionCache(uid, forceRefresh = false) {
    const cacheKey = `subscription_${uid}`;
    
    // # Return cached data if available and not forcing refresh
    if (!forceRefresh) {
      const cachedData = this.get(cacheKey);
      if (cachedData) {
        this._stats.hits++;
        console.log('[CacheManager] Using cached subscription data');
        return cachedData;
      }
    }

    // # Check for pending request
    if (this._pendingRequests.has(cacheKey)) {
      console.log('[CacheManager] Reusing pending subscription data request');
      return this._pendingRequests.get(cacheKey);
    }

    try {
      // # Create promise for this request
      const promise = (async () => {
        console.log('[CacheManager] Fetching subscription data from Firestore');
        
        try {
          // # Simple direct fetch with no retries
          const subscriptionDoc = await firestoreLogger.getDocumentWithLogging(doc(db, 'subscriptions', uid));
          const subscriptionData = subscriptionDoc.exists() ? subscriptionDoc.data() : null;

          if (subscriptionData) {
            console.log('[CacheManager] Subscription document found, caching data');
            // # Ensure subscriptions object exists
            if (!subscriptionData.subscriptions) {
              subscriptionData.subscriptions = {};
            }
            
            // # Cache the data
            this.set(cacheKey, subscriptionData);
            this._clearRelatedAccessCaches(uid);
            return subscriptionData;
          } else {
            console.log('[CacheManager] No subscription document found, caching empty object');
            // # Cache empty subscription data
            const emptyData = { subscriptions: {} };
            this.set(cacheKey, emptyData, 5 * 60 * 1000); // Cache for 5 minutes only
            return emptyData;
          }
        } catch (error) {
          console.error('[CacheManager] Error fetching subscription data:', error);
          // # Return empty data on error
          const emptyData = { subscriptions: {} };
          return emptyData;
        }
      })();

      // # Store promise in pending requests
      this._pendingRequests.set(cacheKey, promise);

      const result = await promise;
      this._pendingRequests.delete(cacheKey);
      return result;
    } catch (error) {
      this._pendingRequests.delete(cacheKey);
      console.error('Error initializing subscription cache:', error);
      // # Return empty data on error
      return { subscriptions: {} };
    }
  }

  // # Initialize library progress cache with deduplication
  async initializeLibraryProgressCache(uid, forceRefresh = false) {
    const cacheKey = `library_progress_${uid}`;
    
    // # Return cached data if available and not forcing refresh
    if (!forceRefresh) {
      const cachedData = this.get(cacheKey);
      if (cachedData) return cachedData;
    }

    // # Check for pending request
    if (this._pendingRequests.has(cacheKey)) {
      return this._pendingRequests.get(cacheKey);
    }

    try {
      // # Create promise for this request
      const promise = (async () => {
        const progressDoc = await firestoreLogger.getDocumentWithLogging(doc(db, 'my_lib_progress', uid));
        const progressData = progressDoc.exists() ? progressDoc.data() : {};

        if (progressDoc.exists()) {
          this.set(cacheKey, progressData);
        }
        
        return progressData;
      })();

      // # Store promise in pending requests
      this._pendingRequests.set(cacheKey, promise);

      const result = await promise;
      this._pendingRequests.delete(cacheKey);
      return result;
    } catch (error) {
      this._pendingRequests.delete(cacheKey);
      console.error('Error initializing library progress cache:', error);
      return {};
    }
  }

  // # Force refresh specific cache
  async refreshCache(uid, type) {
    // # Removed console logging for security and performance
    switch (type) {
      case 'user':
        return this.initializeUserCache(uid, true);
      case 'subscription':
        return this.initializeSubscriptionCache(uid, true);
      case 'library_progress':
        return this.initializeLibraryProgressCache(uid, true);
      default:
        return null;
    }
  }

  // # Add subscription check caching
  async checkSubscriptionAccess(contentType, uid) {
    console.log(`[CacheManager] Checking subscription access for ${contentType}: true (open access)`);
    return true;
  }

  // # Helper to calculate access - always returns true now
  _calculateAccess(contentType, subscriptionData) {
    console.log(`[CacheManager] Access calculation for ${contentType}: true (open access)`);
    return true;
  }

  // # Clear related access caches when subscription changes - no longer needed
  _clearRelatedAccessCaches(uid) {
    // No-op since everything is open access
  }
}

// # Create singleton instance
const cacheManager = new CacheManager();

export default cacheManager;
